/**
 * AI Skill Modal
 *
 * Purpose: Dialog wrapping AISkillForm. Owns form state, validation, save
 *          handlers, and the inline duplicate-name 409 surface. Loads the
 *          tool-call options from /metadata/ai-skills/options at open (ORCH-217).
 * Architecture: Simpler than AIAgentModal — skills are GLOBAL-only, so there is
 *          no tenant context or slug.
 * Author: Development Team
 * Created: 2026-06-10
 */

import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { Delete as DeleteIcon, PsychologyOutlined as SkillIcon } from '@mui/icons-material';

import aiSkillService, {
  AiSkill,
  AiSkillConflict,
  AiSkillCreatePayload,
  AiSkillUpdatePayload,
} from '../../../services/aiSkillService';
import AISkillForm, { AISkillFormData } from '../AISkillForm';
import { SecondaryButton, DestructiveButton } from '../../common/ActionButtons';
import DialogFooter from '../../common/DialogFooter';

export interface AISkillModalProps {
  open: boolean;
  mode: 'create' | 'edit';
  skill?: AiSkill;
  canEdit: boolean;
  onClose: () => void;
  onSaved: () => void;
  onDelete?: (skill: AiSkill) => void;
}

const emptyFormData = (): AISkillFormData => ({
  name: '',
  md_content: '',
  tool_calls: [],
});

export const AISkillModal: React.FC<AISkillModalProps> = ({
  open,
  mode,
  skill,
  canEdit,
  onClose,
  onSaved,
  onDelete,
}) => {
  const [formData, setFormData] = useState<AISkillFormData>(() => emptyFormData());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [duplicate, setDuplicate] = useState<AiSkillConflict | null>(null);
  const [loading, setLoading] = useState(false);
  const [toolCallOptions, setToolCallOptions] = useState<string[]>([]);

  // Load tool-call options once on first open.
  useEffect(() => {
    if (!open) return;
    aiSkillService.getAISkillOptions().then((opts) => {
      setToolCallOptions(opts.tool_calls ?? []);
    });
  }, [open]);

  // Reset state when the modal opens.
  useEffect(() => {
    if (!open) return;
    setErrors({});
    setSaveError(null);
    setDuplicate(null);

    if (mode === 'edit' && skill) {
      setFormData({
        name: skill.name,
        md_content: skill.md_content || '',
        tool_calls: skill.tool_calls ?? [],
      });
    } else {
      setFormData(emptyFormData());
    }
  }, [open, mode, skill]);

  const validate = (): boolean => {
    const next: Record<string, string> = {};

    if (!formData.name.trim()) {
      next.name = 'Name is required';
    } else if (formData.name.trim().length > 200) {
      next.name = 'Name must be under 200 characters';
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    setSaveError(null);
    setDuplicate(null);

    if (!validate()) return;

    setLoading(true);
    try {
      if (mode === 'create') {
        const payload: AiSkillCreatePayload = {
          name: formData.name.trim(),
          md_content: formData.md_content,
          tool_calls: formData.tool_calls,
        };
        await aiSkillService.createAISkill(payload);
      } else if (skill) {
        const payload: AiSkillUpdatePayload = {
          name: formData.name.trim(),
          md_content: formData.md_content,
          tool_calls: formData.tool_calls,
        };
        await aiSkillService.updateAISkill(skill.id, payload);
      }
      onSaved();
      onClose();
    } catch (err) {
      const conflict = aiSkillService.extractConflict(err);
      if (conflict) {
        setDuplicate(conflict);
      } else {
        const message =
          err instanceof Error ? err.message : 'Failed to save skill. Please try again.';
        setSaveError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {
    if (!skill || !onDelete) return;
    onDelete(skill);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { height: '90vh', maxHeight: '90vh' } }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <SkillIcon />
          {mode === 'create' ? 'Create Skill' : 'Edit Skill'}
        </Box>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}
      >
        {saveError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {saveError}
          </Alert>
        )}

        {duplicate && (
          <Alert severity="error" sx={{ mb: 2 }} data-testid="ai-skill-duplicate-alert">
            {duplicate.message}
          </Alert>
        )}

        <AISkillForm
          formData={formData}
          errors={errors}
          canEdit={canEdit}
          toolCallOptions={toolCallOptions}
          onChange={setFormData}
        />
      </DialogContent>

      <DialogFooter
        destructiveAction={
          mode === 'edit' &&
          onDelete &&
          canEdit && (
            <DestructiveButton
              onClick={handleDelete}
              startIcon={<DeleteIcon />}
              disabled={loading}
              data-testid="ai-skill-delete-button"
            >
              Delete
            </DestructiveButton>
          )
        }
      >
        <SecondaryButton onClick={onClose} disabled={loading}>
          Cancel
        </SecondaryButton>
        {canEdit && (
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : undefined}
            data-testid="ai-skill-save-button"
          >
            {mode === 'create' ? 'Create' : 'Update'}
          </Button>
        )}
      </DialogFooter>
    </Dialog>
  );
};

export default AISkillModal;
